import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { authService } from "../../services/authService";
import '../../assets/styles/AuthForm.css';
import '../../assets/styles/bootstrap.min.css';
import i1 from '../../assets/image/i1.png';
import logo from '../../assets/image/logo.png';
import { VALIDATION, ERROR_MESSAGES, ROUTES } from "../../utils/constants";
import Footer from '../../components/Layout/Footer';
const SignInPage: React.FC = () => {
  // State
  const [loginCredentials, setLoginCredentials] = useState({
    email: "",
    password: ""
  });
  const [errors, setErrors] = useState({
    email: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Hooks
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const from = location.state?.from?.pathname || "/";

  const validateEmail = (email: string): string => {
    if (!email) return "Email is required";
    if (!VALIDATION.EMAIL_REGEX.test(email)) return ERROR_MESSAGES.INVALID_EMAIL;
    return "";
  };

  const validatePassword = (password: string): string => {
    if (!password) return "Password is required";
    if (!VALIDATION.PASSWORD_REGEX.test(password)) return ERROR_MESSAGES.PASSWORD_ERROR_MESSAGE;
    return "";
  };

  const validateForm = (): boolean => {
    const emailError = validateEmail(loginCredentials.email);
    const passwordError = validatePassword(loginCredentials.password);

    setErrors({
      email: emailError,
      password: passwordError,
    });

    return !emailError && !passwordError;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const updatedValue = name === "email" ? value.toLowerCase() : value;
    setLoginCredentials((prev) => ({ ...prev, [name]: updatedValue }));
    setError(null);
    // Real-time validation
    if (name === "email") {
      setErrors(prev => ({ ...prev, email: validateEmail(value) }));
    }

    if (name === "password") {
      setErrors(prev => ({ ...prev, password: validatePassword(value) }));
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setInfoMessage(null);
    if (!validateForm()) return;
    try {
      setIsSubmitting(true);
      const response = await authService.login(loginCredentials);
      const redirectPath = response.role_id === 1 ? ROUTES.ADMIN_DASHBOARD : ROUTES.DASHBOARD;
      localStorage.setItem("accessToken", response.access_token);
      localStorage.setItem("refreshToken", response.refresh_token || "");
      localStorage.setItem("role_id", response.role_id.toString());
      localStorage.setItem("userId", response.id.toString());
      localStorage.setItem("email", response.email);
      localStorage.setItem("permissions", JSON.stringify(response.permissions));
      localStorage.setItem("user", JSON.stringify(response));

      navigate(redirectPath);
    } catch (err: any) {
      const errorMessage = getErrorMessage(err);
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getErrorMessage = (err: any): string => {
    if (err.response) {
      const { data, status } = err.response;
      if (data?.details?.[0]?.msg) return data?.details?.[0]?.msg;
      if (data?.message) return data?.message;
      if (status === 401) return "Invalid email or password";
      if (status === 403) return "Account not verified. Please check your email.";
    }
    return "Login failed. Please try again.";
  };

  useEffect(() => {
    const message = searchParams.get('message');
    const unauthorized = searchParams.get('unauthorized');

    if (message) {
      setInfoMessage(message);
    }

    if (unauthorized) {
      setError('You need to login to access that page');
    }
  }, [searchParams]);

  // Display a message if the user was redirected to the login page
  useEffect(() => {
    if (from !== "/") {
      setInfoMessage("You need to log in to access this page.");
      const timer = setTimeout(() => {
        setInfoMessage(null);
      }, 5000);

      // 🧹 Cleanup timer on unmount
      return () => clearTimeout(timer);
    }
  }, [from]);

  return (
    <div className="auth-page" style={{ overflowY: "auto", height: "100vh" }}>
      <div className="auth-form-container">
        <div className="auth-decorative-panel">
          <div className="decorative-content">
            <img src={i1} alt="Corecrate Image" />
          </div>
        </div>

        <div className="auth-form-wrapper mr-5 ml-5">
          <img src={logo} alt="Corecrate Image" className="mb-5" />
          {/* ✅ Show info/warning message */}
          {infoMessage && (
            <div
              style={{
                backgroundColor: "#fff3cd",
                color: "#856404",
                border: "1px solid #ffeeba",
                padding: "10px",
                borderRadius: "5px",
                marginBottom: "15px",
              }}
            >
              ⚠️ {infoMessage}
            </div>
          )}
          <form className="auth-form" onSubmit={handleSubmit}>
            <>
              <div className="form-group">
                <label htmlFor="email">Email address</label>
                <input
                  id="email"
                  type="email"
                  name="email"
                  value={loginCredentials.email}
                  onChange={handleChange}
                  placeholder="Enter your email"
                />
                {errors.email && <span className="error-message">{errors.email}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="password">Password</label>
                <div className="password-wrapper">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    name="password"
                    placeholder="********"
                    value={loginCredentials.password}
                    onChange={handleChange}
                    minLength={8}
                  />
                  <button
                    type="button"
                    className="toggle-password"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? "Hide" : "Show"}
                  </button>
                </div>
                {errors.password && (
                  <span className="error-message">{errors.password}</span>)}
              </div>

            </>
            {error && <div className="error-message">{error}</div>}
            <button type="submit" disabled={isSubmitting} className="submit-button">
              {isSubmitting ? (
                <>
                  <span className="spinner"></span>
                  Loading...
                </>
              ) : (
                'Sign in'
              )}
            </button>

            <div className="form-footer">
              <p>Don't have an account yet? <Link to={ROUTES.SIGNUP}>Create one</Link> <span className="divider">|</span> <Link to={ROUTES.FORGOT_PASSWORD}>Forgot?</Link></p>
            </div>
          </form>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default SignInPage;